import React, { useCallback, useMemo } from "react";
import { Error as ErrorBlock } from "../../../common/Error";
import { HousingItem } from "../../../../pages/Housings/HousingItem";
import type { SelectedVariant } from "../../types";
import { formatRub, installmentLabel } from "../ViewCollection/utils";

type Props = {
    items: SelectedVariant[];
    onRemove: (id: string) => void;
    onSave: () => void;
    saving: boolean;
    savedLink?: string;
    onCopyLink: () => void;
    error?: string;
};

const ensureAbsoluteUrl = (raw?: string | null): string => {
    if (!raw) return "";
    const value = String(raw).trim();
    if (/^https?:\/\//i.test(value)) return value;

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!value.includes("/")) return `${origin}/collection/${encodeURIComponent(value)}`;
    return `${origin}${value.startsWith("/") ? "" : "/"}${value}`;
};

export const SelectionView: React.FC<Props> = ({
    items,
    onRemove,
    onSave,
    saving,
    savedLink,
    onCopyLink,
    error,
}) => {
    const linkValue = useMemo(() => ensureAbsoluteUrl(savedLink), [savedLink]);

    const handleShare = useCallback(async () => {
        if (!linkValue) return;
        const shareData = {
            title: "Подборка квартир",
            text: "Посмотри варианты, которые я для тебя подобрал(а).",
            url: linkValue,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch {
                // User dismissed the native share sheet.
            }
            return;
        }

        onCopyLink();
    }, [linkValue, onCopyLink]);

    return (
        <div className="ai-selection-workspace">
            {error && <ErrorBlock title="Не удалось сохранить подборку" description={error} />}

            <div className="ai-selection-status">
                <div>
                    <strong>{items.length} из 3 вариантов</strong>
                    <span>Готово к отправке клиенту</span>
                </div>
                <span className="ai-selection-status__hint">Ссылка будет доступна только после сохранения</span>
            </div>

            {items.length ? (
                <ul className="ai-selection-list">
                    {items.map((variant) => {
                        const housing = variant.housing || {
                            id: variant.housingId,
                            housingName: "Жилой комплекс",
                            images: variant.images ?? [],
                        };
                        const paymentLabel = installmentLabel(
                            variant.installmentPlan,
                            variant.installmentPeriod,
                        );
                        const monthlyPayment = typeof variant.monthlyPrice === "number"
                            ? `от ${formatRub(Math.round(variant.monthlyPrice))} / мес.`
                            : paymentLabel;

                        return (
                            <HousingItem
                                key={variant.id}
                                housing={housing}
                                className="ai-selection-card"
                                meta={(
                                    <>
                                        <span className="housing-item_meta-item">
                                            {variant.roomType || "Планировка"}
                                            {variant.area != null ? ` · ${variant.area} м²` : ""}
                                        </span>
                                        {variant.floorRange && (
                                            <span className="housing-item_meta-item">Этаж: {variant.floorRange}</span>
                                        )}
                                    </>
                                )}
                                price={{
                                    label: "Выбранный вариант",
                                    value: formatRub(variant.fullPrice),
                                    note: monthlyPayment,
                                }}
                                action={(
                                    <button
                                        type="button"
                                        className="ai-selection-card__remove"
                                        onClick={() => onRemove(variant.id)}
                                    >
                                        Убрать
                                    </button>
                                )}
                            />
                        );
                    })}
                </ul>
            ) : (
                <div className="ai-selection-empty">
                    <strong>Подборка пока пуста</strong>
                    <span>Вернитесь к поиску и выберите варианты в карточке ЖК.</span>
                </div>
            )}

            <section className="ai-selection-actions" aria-label="Сохранение подборки">
                {!savedLink ? (
                    <>
                        <div>
                            <strong>Подборка готова</strong>
                            <span>Сохраните её, чтобы получить ссылку для клиента.</span>
                        </div>
                        <button
                            type="button"
                            className="ai-selection-actions__primary"
                            onClick={onSave}
                            disabled={!items.length || saving}
                        >
                            {saving ? "Сохраняю…" : "Сохранить и получить ссылку"}
                        </button>
                    </>
                ) : (
                    <>
                        <div>
                            <strong>Ссылка готова</strong>
                            <span>Её можно скопировать, открыть или отправить клиенту.</span>
                        </div>
                        <div className="ai-selection-actions__share">
                            <input value={linkValue} readOnly aria-label="Ссылка на подборку" />
                            <button type="button" onClick={onCopyLink}>Копировать</button>
                            <button type="button" onClick={handleShare}>Отправить</button>
                            <button type="button" onClick={() => window.open(linkValue, "_blank")}>Открыть</button>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};
